import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { useAuth } from '../context/AuthContext';
import { fmtTs } from '../lib/globalTz';

interface CopilotConfig {
  customerId: string;
  enabled: boolean;
  mode: 'mock' | 'live';
  defaultPromptbookId: string | null;
  defaultAgentId: string | null;
  region: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}
interface ConfigResponse { config: CopilotConfig; serverLiveEnabled: boolean; }
interface Promptbook { id: string; name: string; description: string | null; tags: string[]; plugin: string | null; }
interface Agent { id: string; name: string; description: string | null; publisher: string | null; workload: string | null; flagship: boolean; }
interface ListResponse<T> {
  items: T[];
  mode: { configured: 'mock' | 'live'; effective: 'mock' | 'live'; reason: string | null };
}
interface Session {
  id: string;
  customerId: string;
  incidentId: string | null;
  kind: 'promptbook' | 'agent' | 'prompt';
  refId: string | null;
  prompt: { kind: string; refId: string | null; prompt: string; context: unknown };
  response: { output: string; sessionId: string | null; scuCost: string | null; mode: string } | null;
  status: 'pending' | 'succeeded' | 'failed';
  errorMessage: string | null;
  scuCost: string | null;
  latencyMs: number | null;
  mode: 'mock' | 'live';
  createdBy: string;
  createdAt: string;
  completedAt: string | null;
}
interface RunResponse {
  sessionId: string;
  output: string;
  mode: 'mock' | 'live';
  scuCost: string | null;
  latencyMs: number;
}

export const copilotConfigKey = (customerId: string) => ['copilot', 'config', customerId] as const;
export const copilotPromptbooksKey = (customerId: string) => ['copilot', 'promptbooks', customerId] as const;
export const copilotAgentsKey = (customerId: string) => ['copilot', 'agents', customerId] as const;
export const copilotSessionsKey = (customerId: string) => ['copilot', 'sessions', customerId] as const;

/** Flagship Microsoft Security Copilot agent ids we surface as one-click cards.
 *  Live responses don't carry the `flagship` flag, so we lift them by id here. */
const FLAGSHIP_AGENT_IDS = new Set([
  'ag_phishing_triage',
  'ag_threat_intel_briefing',
]);

/* Compact workload label used as the agent-card marker. */
function workloadMarker(workload: string | null): string {
  switch (workload) {
    case 'Microsoft Defender': return 'MDE';
    case 'Microsoft Defender Threat Intelligence': return 'MDTI';
    case 'Microsoft Purview': return 'PUR';
    case 'Microsoft Intune': return 'INT';
    case 'Microsoft Entra': return 'ENT';
    case 'Microsoft Sentinel': return 'SEN';
    default: return 'AI';
  }
}

export function SecurityCopilotTab() {
  const { activeCustomerId } = useCustomers();
  const { canWrite, canCapability } = useAuth();
  const queryClient = useQueryClient();
  const [view, setView] = useState<'configure' | 'agents' | 'promptbooks' | 'sessions'>('configure');
  const [error, setError] = useState<string | null>(null);
  const [runResult, setRunResult] = useState<{ title: string; output: string; mode: string; latencyMs: number; scuCost: string | null } | null>(null);

  const hasTabWrite = canWrite('security_copilot', activeCustomerId);
  const canConfigure = hasTabWrite && canCapability('security_copilot', 'configure');
  const canRun = hasTabWrite && canCapability('security_copilot', 'run_copilot');
  const canViewSessions = canCapability('security_copilot', 'view_sessions');

  const configQuery = useQuery({
    queryKey: copilotConfigKey(activeCustomerId),
    queryFn: () => apiFetch<ConfigResponse>(`/copilot/config/${encodeURIComponent(activeCustomerId)}`),
    enabled: !!activeCustomerId,
  });

  const enabled = !!configQuery.data?.config.enabled;

  const promptbooksQuery = useQuery({
    queryKey: copilotPromptbooksKey(activeCustomerId),
    queryFn: () => apiFetch<ListResponse<Promptbook>>(`/copilot/promptbooks/${encodeURIComponent(activeCustomerId)}`),
    enabled: !!activeCustomerId && enabled,
    retry: false,
  });

  const agentsQuery = useQuery({
    queryKey: copilotAgentsKey(activeCustomerId),
    queryFn: () => apiFetch<ListResponse<Agent>>(`/copilot/agents/${encodeURIComponent(activeCustomerId)}`),
    enabled: !!activeCustomerId && enabled,
    retry: false,
  });

  const sessionsQuery = useQuery({
    queryKey: copilotSessionsKey(activeCustomerId),
    queryFn: () => apiFetch<{ sessions: Session[] }>(`/copilot/sessions/${encodeURIComponent(activeCustomerId)}?limit=100`),
    enabled: !!activeCustomerId && view === 'sessions' && canViewSessions,
  });

  const saveConfig = useMutation({
    mutationFn: (next: Partial<CopilotConfig>) =>
      apiFetch<ConfigResponse>(`/copilot/config/${encodeURIComponent(activeCustomerId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(copilotConfigKey(activeCustomerId), data);
      queryClient.invalidateQueries({ queryKey: copilotPromptbooksKey(activeCustomerId) });
      queryClient.invalidateQueries({ queryKey: copilotAgentsKey(activeCustomerId) });
      setError(null);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });

  const runMutation = useMutation({
    mutationFn: (vars: { kind: 'promptbook' | 'agent'; refId: string; prompt: string; title: string }) =>
      apiFetch<RunResponse>('/copilot/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: activeCustomerId,
          kind: vars.kind,
          refId: vars.refId,
          prompt: vars.prompt,
        }),
      }).then((r) => ({ ...r, title: vars.title })),
    onSuccess: (data) => {
      setRunResult({ title: data.title, output: data.output, mode: data.mode, latencyMs: data.latencyMs, scuCost: data.scuCost });
      setError(null);
      queryClient.invalidateQueries({ queryKey: copilotSessionsKey(activeCustomerId) });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Run failed'),
  });

  if (!activeCustomerId) {
    return (
      <div className="p-6 text-sm text-white">
        Select a customer to configure Security Copilot.
      </div>
    );
  }

  const config = configQuery.data?.config;
  const serverLiveEnabled = configQuery.data?.serverLiveEnabled ?? false;
  const agents = agentsQuery.data?.items ?? [];
  const promptbooks = promptbooksQuery.data?.items ?? [];
  const flagshipAgents = agents.filter((a) => a.flagship || FLAGSHIP_AGENT_IDS.has(a.id));

  return (
    <div className="p-4 space-y-4">
      {/* Branded header - Copilot purple gradient sets it visually apart from
          the other SOC tabs and signals this surface is a Microsoft Security
          Copilot integration, not native Kloudsecure AI. */}
      <div className="rounded-lg overflow-hidden shadow bg-gradient-to-r from-indigo-700 via-purple-700 to-fuchsia-700 text-white p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-xl font-bold">Microsoft Security Copilot</h2>
              <span className="text-xs px-2 py-0.5 rounded bg-white/20 font-medium">
                {enabled ? (config?.mode === 'live' && serverLiveEnabled ? 'LIVE' : 'MOCK') : 'DISABLED'}
              </span>
            </div>
            <p className="text-sm text-purple-100">
              Run Microsoft promptbooks and first-party agents (Phishing Triage, Threat Intelligence Briefing, and more) against this customer's tenant from inside Kloudsecure.
            </p>
          </div>
          <div className="flex gap-1 rounded-md bg-white/10 p-1 shrink-0">
            {(['configure', 'agents', 'promptbooks', 'sessions'] as const).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1 text-xs font-medium rounded ${view === v ? 'bg-white text-purple-700' : 'text-white hover:bg-white/10'}`}
              >
                {v === 'configure' ? 'Configure' : v === 'agents' ? 'Agents' : v === 'promptbooks' ? 'Promptbooks' : 'Sessions'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-300 bg-red-50 dark:bg-red-900/30 dark:border-red-700 p-3 text-sm text-red-700 dark:text-red-200">
          {error}
        </div>
      )}

      {!serverLiveEnabled && (
        <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/30 dark:border-amber-700 p-3 text-sm text-amber-800 dark:text-amber-100">
          Server-wide kill switch <code className="font-mono">COPILOT_LIVE</code> is off. All customers run in mock mode regardless of their per-customer setting.
        </div>
      )}

      {view === 'configure' && config && (
        <ConfigureView
          config={config}
          serverLiveEnabled={serverLiveEnabled}
          flagshipAgents={flagshipAgents}
          canEdit={canConfigure}
          saving={saveConfig.isPending}
          onSave={(next) => saveConfig.mutate(next)}
        />
      )}

      {view === 'agents' && (
        <AgentsView
          agents={agents}
          enabled={enabled}
          canRun={canRun}
          modeBanner={agentsQuery.data?.mode ?? null}
          loadError={agentsQuery.error instanceof Error ? agentsQuery.error.message : null}
          loading={agentsQuery.isLoading}
          onRun={(a) => runMutation.mutate({
            kind: 'agent',
            refId: a.id,
            prompt: `Run the ${a.name} workflow against this customer's tenant and return its standard output.`,
            title: a.name,
          })}
          running={runMutation.isPending}
        />
      )}

      {view === 'promptbooks' && (
        <PromptbooksView
          promptbooks={promptbooks}
          enabled={enabled}
          canRun={canRun}
          modeBanner={promptbooksQuery.data?.mode ?? null}
          loadError={promptbooksQuery.error instanceof Error ? promptbooksQuery.error.message : null}
          loading={promptbooksQuery.isLoading}
          onRun={(p) => runMutation.mutate({
            kind: 'promptbook',
            refId: p.id,
            prompt: `Execute the "${p.name}" promptbook against this customer's tenant with default inputs.`,
            title: p.name,
          })}
          running={runMutation.isPending}
        />
      )}

      {view === 'sessions' && canViewSessions && (
        <SessionsView sessions={sessionsQuery.data?.sessions ?? []} loading={sessionsQuery.isLoading} />
      )}

      {runResult && (
        <RunResultModal result={runResult} onClose={() => setRunResult(null)} />
      )}
    </div>
  );
}

// ── Configure view ──────────────────────────────────────────────────────

function ConfigureView({
  config,
  serverLiveEnabled,
  flagshipAgents,
  canEdit,
  saving,
  onSave,
}: {
  config: CopilotConfig;
  serverLiveEnabled: boolean;
  flagshipAgents: Agent[];
  canEdit: boolean;
  saving: boolean;
  onSave: (next: Partial<CopilotConfig>) => void;
}) {
  const [draft, setDraft] = useState<CopilotConfig>(config);
  const dirty = JSON.stringify(draft) !== JSON.stringify(config);
  const effectiveMode = draft.mode === 'live' && !serverLiveEnabled ? 'mock' : draft.mode;

  return (
    <div className="space-y-4">
      {/* Setup checklist - surfaces the exact SPN consent + scope work an
          MSSP admin needs to do per Microsoft's Copilot REST API docs. */}
      <ChecklistPanel config={config} serverLiveEnabled={serverLiveEnabled} />

      {/* Flagship Microsoft agents - one-click cards for the two most-used
          first-party agents (Phishing Triage, Threat Intelligence Briefing). */}
      {flagshipAgents.length > 0 && config.enabled && (
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
            Flagship Microsoft agents
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {flagshipAgents.map((a) => (
              <FlagshipAgentCard
                key={a.id}
                agent={a}
                isDefault={config.defaultAgentId === a.id}
                onSetDefault={canEdit ? () => onSave({ ...config, defaultAgentId: a.id }) : null}
              />
            ))}
          </div>
        </div>
      )}

      {/* Enable + mode controls */}
      <div className="rounded border border-gray-200 dark:border-gray-700 p-4 space-y-3 bg-white dark:bg-gray-900/40">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={draft.enabled}
            onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
          />
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            Security Copilot enabled for this customer
          </span>
        </label>
        <p className="text-xs text-gray-700 dark:text-gray-100 ml-6">
          When off, the per-incident "Ask Copilot" button is hidden and the Sessions tab is empty.
        </p>

        <div className="ml-6 space-y-2 pt-2 border-t border-gray-100 dark:border-gray-800">
          <div className="text-sm font-medium text-gray-900 dark:text-white">Execution mode</div>
          {(['mock', 'live'] as const).map((m) => (
            <label key={m} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                disabled={!canEdit}
                checked={draft.mode === m}
                onChange={() => setDraft({ ...draft, mode: m })}
              />
              <span>
                <span className="font-medium text-gray-900 dark:text-white">{m === 'mock' ? 'Mock' : 'Live'}</span>
                <span className="block text-xs text-gray-700 dark:text-gray-100">
                  {m === 'mock'
                    ? 'Synthetic responses. No SCU cost. No tenant setup required. Use this to demo the workflow or train new analysts.'
                    : 'Calls the real Microsoft Security Copilot REST API using this customer\'s existing SPN. Requires SecurityCopilot.ReadWrite.All admin consent on the customer tenant. Each run consumes SCU credits from the customer subscription.'}
                </span>
              </span>
            </label>
          ))}
          {draft.mode === 'live' && !serverLiveEnabled && (
            <div className="text-xs text-amber-700 dark:text-amber-200 ml-6 mt-1">
              Heads up: COPILOT_LIVE env flag is off, so this customer will still resolve to <strong>{effectiveMode}</strong> until ops flips the server-wide switch.
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={() => onSave({
            enabled: draft.enabled,
            mode: draft.mode,
            defaultPromptbookId: draft.defaultPromptbookId,
            defaultAgentId: draft.defaultAgentId,
            region: draft.region,
          })}
          disabled={!canEdit || !dirty || saving}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded disabled:opacity-50 text-sm font-medium"
        >
          {saving ? 'Saving...' : 'Save configuration'}
        </button>
        {!canEdit && (
          <span className="text-xs text-gray-700 dark:text-gray-100">
            Read-only (you do not have Write on Security Copilot for this customer).
          </span>
        )}
        {config.updatedAt && (
          <span className="text-xs text-gray-700 dark:text-gray-100">
            Last updated {fmtTs(config.updatedAt)}
            {config.updatedBy ? ` by ${config.updatedBy}` : ''}
          </span>
        )}
      </div>
    </div>
  );
}

function ChecklistPanel({ config, serverLiveEnabled }: { config: CopilotConfig; serverLiveEnabled: boolean }) {
  const items: Array<{ ok: boolean; label: string; hint: string }> = [
    {
      ok: config.enabled,
      label: 'Per-customer toggle enabled',
      hint: 'Flips on the tab + the per-incident Ask Copilot button for users scoped to this customer.',
    },
    {
      ok: config.mode === 'live',
      label: 'Mode set to live',
      hint: 'Switch from mock to live once the SPN consent below is granted on the customer tenant.',
    },
    {
      ok: serverLiveEnabled,
      label: 'COPILOT_LIVE env flag on (server-side)',
      hint: 'MSSP ops sets this env var on the API server to flip the global kill switch off.',
    },
    {
      ok: false,
      label: 'SPN granted SecurityCopilot.ReadWrite.All (manual)',
      hint: 'In the customer\'s Entra tenant, grant the existing Kloudsecure SPN the SecurityCopilot.ReadWrite.All application permission with admin consent. Kloudsecure reuses the same SPN you already use for Sentinel + Defender, so no second secret is needed.',
    },
    {
      ok: false,
      label: 'Customer has an active Security Copilot SCU subscription (manual)',
      hint: 'Security Copilot is billed per Security Compute Unit (SCU). The customer needs at least one provisioned SCU capacity for live runs to succeed.',
    },
  ];
  return (
    <div className="rounded border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950/30 p-4">
      <div className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-2">Setup checklist</div>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it.label} className="flex items-start gap-2 text-sm">
            <span className={`mt-0.5 text-base ${it.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-500'}`}>
              {it.ok ? '✓' : '○'}
            </span>
            <span>
              <span className={`font-medium ${it.ok ? 'text-gray-900 dark:text-white' : 'text-gray-900 dark:text-white'}`}>{it.label}</span>
              <span className="block text-xs text-gray-700 dark:text-gray-100">{it.hint}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FlagshipAgentCard({
  agent,
  isDefault,
  onSetDefault,
}: {
  agent: Agent;
  isDefault: boolean;
  onSetDefault: (() => void) | null;
}) {
  return (
    <div className={`rounded-lg border p-4 ${isDefault ? 'border-purple-400 bg-purple-50 dark:bg-purple-950/40' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40'}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-11 rounded bg-purple-100 px-2 py-1 text-center text-xs font-bold text-purple-800 dark:bg-purple-900/50 dark:text-purple-100">
          {workloadMarker(agent.workload)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{agent.name}</h3>
            {isDefault && (
              <span className="text-xs px-2 py-0.5 rounded bg-purple-600 text-white">Default</span>
            )}
          </div>
          {agent.workload && (
            <div className="text-xs text-gray-700 dark:text-gray-100 mt-0.5">{agent.workload}</div>
          )}
          {agent.description && (
            <p className="text-xs text-gray-700 dark:text-gray-100 mt-2 leading-relaxed">{agent.description}</p>
          )}
          {onSetDefault && !isDefault && (
            <button
              onClick={onSetDefault}
              className="mt-3 text-xs text-purple-700 dark:text-purple-300 hover:underline"
            >
              Set as default for incident drawer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Agents view ─────────────────────────────────────────────────────────

function AgentsView({
  agents, enabled, canRun, modeBanner, loadError, loading, onRun, running,
}: {
  agents: Agent[];
  enabled: boolean;
  canRun: boolean;
  modeBanner: ListResponse<unknown>['mode'] | null;
  loadError: string | null;
  loading: boolean;
  onRun: (agent: Agent) => void;
  running: boolean;
}) {
  if (!enabled) return <DisabledHint label="agents" />;
  if (loadError) return <div className="text-sm text-red-700 dark:text-red-200">{loadError}</div>;
  if (loading) return <div className="text-sm text-white">Loading agents...</div>;
  return (
    <div className="space-y-3">
      <ModeBanner banner={modeBanner} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {agents.map((a) => (
          <div key={a.id} className="rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-4">
            <div className="flex items-start gap-3">
              <div className="min-w-11 rounded bg-purple-100 px-2 py-1 text-center text-xs font-bold text-purple-800 dark:bg-purple-900/50 dark:text-purple-100">
                {workloadMarker(a.workload)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{a.name}</h3>
                  {a.publisher && (
                    <span className={`text-[10px] px-2 py-0.5 rounded ${a.publisher === 'Microsoft' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-100' : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'}`}>
                      {a.publisher}
                    </span>
                  )}
                </div>
                {a.workload && (
                  <div className="text-xs text-gray-700 dark:text-gray-100 mt-0.5">{a.workload}</div>
                )}
                {a.description && (
                  <p className="text-xs text-gray-700 dark:text-gray-100 mt-2 leading-relaxed">{a.description}</p>
                )}
                <button
                  onClick={() => onRun(a)}
                  disabled={!canRun || running}
                  className="mt-3 px-3 py-1.5 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded disabled:opacity-50"
                >
                  {running ? 'Running...' : 'Run agent'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Promptbooks view ────────────────────────────────────────────────────

function PromptbooksView({
  promptbooks, enabled, canRun, modeBanner, loadError, loading, onRun, running,
}: {
  promptbooks: Promptbook[];
  enabled: boolean;
  canRun: boolean;
  modeBanner: ListResponse<unknown>['mode'] | null;
  loadError: string | null;
  loading: boolean;
  onRun: (p: Promptbook) => void;
  running: boolean;
}) {
  if (!enabled) return <DisabledHint label="promptbooks" />;
  if (loadError) return <div className="text-sm text-red-700 dark:text-red-200">{loadError}</div>;
  if (loading) return <div className="text-sm text-white">Loading promptbooks...</div>;
  // Group by plug-in so analysts can see workload coverage at a glance.
  const byPlugin = new Map<string, Promptbook[]>();
  for (const p of promptbooks) {
    const k = p.plugin ?? 'Other';
    const arr = byPlugin.get(k) ?? [];
    arr.push(p);
    byPlugin.set(k, arr);
  }
  return (
    <div className="space-y-4">
      <ModeBanner banner={modeBanner} />
      {Array.from(byPlugin.entries()).map(([plugin, items]) => (
        <div key={plugin}>
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-100 mb-2">{plugin}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map((p) => (
              <div key={p.id} className="rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{p.name}</h3>
                {p.description && (
                  <p className="text-xs text-gray-700 dark:text-gray-100 mt-1 leading-relaxed">{p.description}</p>
                )}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {p.tags.map((t) => (
                    <span key={t} className="text-[10px] px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100">{t}</span>
                  ))}
                </div>
                <button
                  onClick={() => onRun(p)}
                  disabled={!canRun || running}
                  className="mt-3 px-3 py-1.5 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded disabled:opacity-50"
                >
                  {running ? 'Running...' : 'Run promptbook'}
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ModeBanner({ banner }: { banner: ListResponse<unknown>['mode'] | null }) {
  if (!banner) return null;
  const mock = banner.effective === 'mock';
  return (
    <div className={`text-xs px-3 py-2 rounded inline-flex items-center gap-2 ${mock ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-100' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100'}`}>
      <span className="font-semibold">{mock ? 'Mock catalog' : 'Live catalog from customer tenant'}</span>
      {banner.reason && <span className="opacity-80">({banner.reason})</span>}
    </div>
  );
}

function DisabledHint({ label }: { label: string }) {
  return (
    <div className="rounded border border-dashed border-gray-300 dark:border-gray-700 p-6 text-center text-sm text-gray-700 dark:text-gray-100">
      Security Copilot is not enabled for this customer. Turn it on in the Configure tab to load {label}.
    </div>
  );
}

// ── Sessions view ───────────────────────────────────────────────────────

function SessionsView({ sessions, loading }: { sessions: Session[]; loading: boolean }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  if (loading) return <div className="text-sm text-white">Loading sessions...</div>;
  if (sessions.length === 0) {
    return (
      <div className="text-sm text-gray-700 dark:text-gray-100 p-6 text-center border border-dashed border-gray-300 dark:border-gray-700 rounded">
        No Copilot sessions yet. Run a promptbook from the Promptbooks tab, an agent from the Agents tab, or use the Ask Copilot button on an incident.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-gray-700 dark:text-gray-100">
          <tr>
            <th className="px-2 py-1">When</th>
            <th className="px-2 py-1">Kind</th>
            <th className="px-2 py-1">Ref</th>
            <th className="px-2 py-1">Incident</th>
            <th className="px-2 py-1">Status</th>
            <th className="px-2 py-1">Mode</th>
            <th className="px-2 py-1">SCU</th>
            <th className="px-2 py-1">Latency</th>
            <th className="px-2 py-1">By</th>
          </tr>
        </thead>
        <tbody>
          {sessions.flatMap((s) => {
            const rows = [
              <tr
                key={s.id}
                className="border-t border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                onClick={() => setExpanded(expanded === s.id ? null : s.id)}
              >
                <td className="px-2 py-1 text-xs text-gray-900 dark:text-white">{fmtTs(s.createdAt)}</td>
                <td className="px-2 py-1 text-gray-900 dark:text-white">{s.kind}</td>
                <td className="px-2 py-1 font-mono text-xs text-gray-900 dark:text-white">{s.refId ?? '-'}</td>
                <td className="px-2 py-1 font-mono text-xs text-gray-900 dark:text-white">{s.incidentId ?? '-'}</td>
                <td className="px-2 py-1">
                  <span className={`text-xs px-2 py-0.5 rounded ${s.status === 'succeeded' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100' : s.status === 'failed' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-100' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-100'}`}>
                    {s.status}
                  </span>
                </td>
                <td className="px-2 py-1 text-xs text-gray-900 dark:text-white">{s.mode}</td>
                <td className="px-2 py-1 text-xs text-gray-900 dark:text-white">{s.scuCost ?? '-'}</td>
                <td className="px-2 py-1 text-xs text-gray-900 dark:text-white">{s.latencyMs != null ? `${s.latencyMs}ms` : '-'}</td>
                <td className="px-2 py-1 text-xs text-gray-900 dark:text-white">{s.createdBy}</td>
              </tr>,
            ];
            if (expanded === s.id) {
              rows.push(
                <tr key={`${s.id}-detail`}>
                  <td colSpan={9} className="bg-gray-50 dark:bg-gray-900/30 p-3">
                    <div className="text-xs font-semibold text-gray-900 dark:text-white mb-1">Prompt</div>
                    <pre className="text-xs whitespace-pre-wrap bg-white dark:bg-gray-950 p-2 rounded border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100">{s.prompt?.prompt ?? ''}</pre>
                    {s.response?.output && (
                      <>
                        <div className="text-xs font-semibold text-gray-900 dark:text-white mt-2 mb-1">Response</div>
                        <pre className="text-xs whitespace-pre-wrap bg-white dark:bg-gray-950 p-2 rounded border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100">{s.response.output}</pre>
                      </>
                    )}
                    {s.errorMessage && (
                      <>
                        <div className="text-xs font-semibold text-red-700 dark:text-red-300 mt-2 mb-1">Error</div>
                        <pre className="text-xs whitespace-pre-wrap bg-red-50 dark:bg-red-900/30 p-2 rounded border border-red-200 dark:border-red-800 text-red-800 dark:text-red-100">{s.errorMessage}</pre>
                      </>
                    )}
                  </td>
                </tr>
              );
            }
            return rows;
          })}
        </tbody>
      </table>
    </div>
  );
}

function RunResultModal({
  result,
  onClose,
}: {
  result: { title: string; output: string; mode: string; latencyMs: number; scuCost: string | null };
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{result.title}</h3>
            <p className="text-xs text-gray-700 dark:text-gray-100">
              {result.mode} - {result.latencyMs}ms{result.scuCost ? ` - ${result.scuCost} SCU` : ''}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none">x</button>
        </div>
        <pre className="text-xs whitespace-pre-wrap text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-950 p-3 rounded border border-gray-200 dark:border-gray-700">{result.output}</pre>
      </div>
    </div>
  );
}
